import { Component, Prop, h, State, Event, EventEmitter, Element, Watch } from '@stencil/core';
import { ButtonVariant, ButtonSize, ButtonState } from './types';
import { VideoPortalManager } from '../../utils/video-portal-utils';

@Component({
  tag: 'vsr-button-v2',
  styleUrl: 'vsr-button.scss',
  shadow: false,
})
export class VsrButton {
  @Element() el: HTMLElement;
  @Prop() variant: ButtonVariant = 'primary';
  @Prop() themeOverride: string = '';
  @Prop() size: ButtonSize = 'M';
  @Prop() state: ButtonState = 'active';
  @Prop() disabled: boolean = false;
  @Prop() target: string;
  @Prop() ariaLabel: string;
  @Prop() href: string;
  @Prop() ctaLinkType: string = '';
  @Prop() ctaVideoUrl: string = '';
  @Prop() ctaVideoTitle: string = '';
  @Prop() contextDescription: string = ''; // Optional context to add to aria-label

  @State() isFocused: boolean = false;

  @Event() vsrClick: EventEmitter<MouseEvent>;

  // Track modal open state
  @State() isModalOpen: boolean = false;

  // Video portal manager instance
  private videoPortalManager: VideoPortalManager = new VideoPortalManager();

  /**
   * Watch for changes to isModalOpen to manage the portal
   */
  @Watch('isModalOpen')
  watchIsModalOpen(newValue: boolean) {
    if (newValue) {
      this.videoPortalManager.openModal({
        videoTitle: this.ctaVideoTitle,
        videoUrl: this.ctaVideoUrl,
        theme: this.themeOverride === 'dark' ? 'dark' : 'light',
        mediaAspectRatio: '16:9',
        videoSize: 'L',
      });

      // Add event listener for modal closed
      window.addEventListener('modalClosed', this.handleModalClosed);
    } else {
      // Remove event listener
      window.removeEventListener('modalClosed', this.handleModalClosed);

      this.videoPortalManager.closeModal();
    }
  }

  /**
   * Component lifecycle method that runs when the component is removed from the DOM
   */
  disconnectedCallback() {
    // Remove event listener
    window.removeEventListener('modalClosed', this.handleModalClosed);

    // Clean up the portal container
    this.videoPortalManager.closeModal();
  }

  private handleClick = (event: MouseEvent) => {
    this.vsrClick.emit(event);

    if (this.target === 'modal' && this.href) {
      event.preventDefault();

      const width = 1280;
      const height = Math.round((width * 9) / 16);

      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      window.open(
        this.href,
        'popupWindow',
        `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`
      );

      return;
    }

    if (this.ctaLinkType === 'video' && this.ctaVideoUrl) {
      // Prevent default behavior if it's a link
      if (this.href) {
        event.preventDefault();
      }
      this.isModalOpen = true;
    }
  };

  /**
   * Handle modal closed event
   */
  private handleModalClosed = () => {
    this.isModalOpen = false;
  };

  /**
   * Get the appropriate button class based on target and link type
   */
  private getTargetClass(): string {
    // First check if it's a video link
    if (this.ctaLinkType === 'video' && this.ctaVideoUrl) {
      return 'vsr-button-video';
    }

    // Check if it's an external link
    if (this.ctaLinkType === 'external') {
      return 'vsr-button-external';
    }

    // Then check the target attribute
    if (!this.target) return '';

    switch (this.target) {
      case '_self':
        return 'vsr-button-same-window';
      case '_blank':
        return 'vsr-button-new-window';
      case 'modal':
        return 'vsr-button-modal';
      default:
        return '';
    }
  }

  private renderButton() {
    const buttonClass = `analytics-button button ${this.variant} ${this.size.toLowerCase()} ${this.state.toLowerCase()} ${this.isFocused ? 'focused' : ''} ${this.getTargetClass()}`;

    // Generate aria-label based on slot content and context if provided
    const generateAriaLabel = () => {
      const slotContent = this.el.textContent?.trim() || '';
      return this.contextDescription ? `${slotContent}: ${this.contextDescription}` : slotContent;
    };

    // Render as anchor tag if href is provided, otherwise as button
    if (this.href) {
      return (
        <a
          data-theme={this.themeOverride}
          class={buttonClass}
          href={this.href}
          target={this.target}
          onClick={e => this.handleClick(e)}
          onFocus={() => (this.isFocused = true)}
          onBlur={() => (this.isFocused = false)}
          aria-disabled={this.state === 'inactive' || this.disabled}
          aria-label={generateAriaLabel()}
        >
          <span class="button-content">
            <slot />
          </span>
          {this.isFocused && <div class="focus-ring"></div>}
        </a>
      );
    }

    return (
      <button
        data-theme={this.themeOverride}
        class={buttonClass}
        onClick={e => this.handleClick(e)}
        disabled={this.state === 'inactive' || this.disabled}
        onFocus={() => (this.isFocused = true)}
        onBlur={() => (this.isFocused = false)}
        aria-disabled={this.state === 'inactive' || this.disabled}
        aria-label={generateAriaLabel()}
      >
        <span class="button-content">
          <slot />
        </span>
        {this.isFocused && <div class="focus-ring"></div>}
      </button>
    );
  }

  render() {
    // We only render the button, the modal is rendered in the portal
    return this.renderButton();
  }
}
